import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    ProgressBar, Tooltip, OverlayTrigger, InputGroup, Form
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function EvaluationFournisseurs() {
    const navigate = useNavigate();
    const [evaluations, setEvaluations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'date_evaluation', direction: 'desc' });

    useEffect(() => {
        fetchEvaluations();
        fetchStats();
    }, []);

    const fetchEvaluations = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?action=evaluations&limit=100`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setEvaluations(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getNoteStars = (note) => {
        const stars = [];
        const roundedNote = Math.round(note);
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <i key={i} className={`ri-star-${i <= roundedNote ? 'fill' : 'line'} text-warning`} 
                   style={{ fontSize: '1rem', marginRight: '2px' }}></i>
            );
        }
        return stars;
    };

    const getNoteBadge = (note) => {
        if (note >= 4.5) return { color: '#10b981', label: 'Excellent', icon: 'ri-star-fill' };
        if (note >= 4) return { color: '#10b981', label: 'Très bon', icon: 'ri-star-fill' };
        if (note >= 3) return { color: '#f59e0b', label: 'Bon', icon: 'ri-star-half-fill' };
        if (note >= 2) return { color: '#ef4444', label: 'Médiocre', icon: 'ri-star-line' };
        return { color: '#ef4444', label: 'Mauvais', icon: 'ri-star-line' };
    };

    const getCriterionColor = (value) => {
        if (value >= 4) return '#10b981';
        if (value >= 3) return '#f59e0b';
        return '#ef4444';
    };

    const getCriterionIcon = (value) => {
        if (value >= 4) return 'ri-emotion-happy-line';
        if (value >= 3) return 'ri-emotion-normal-line';
        return 'ri-emotion-unhappy-line';
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)} 
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const filteredEvaluations = evaluations
        .filter(e => 
            e.fournisseur_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
            e.code_fournisseur?.toLowerCase().includes(filterText.toLowerCase()) ||
            e.commentaire?.toLowerCase().includes(filterText.toLowerCase())
        )
        .sort((a, b) => {
            if (sortConfig.key === 'date_evaluation') {
                return sortConfig.direction === 'asc' 
                    ? new Date(a.date_evaluation) - new Date(b.date_evaluation)
                    : new Date(b.date_evaluation) - new Date(a.date_evaluation);
            }
            if (sortConfig.key === 'note') {
                return sortConfig.direction === 'asc' 
                    ? (a.note || 0) - (b.note || 0)
                    : (b.note || 0) - (a.note || 0);
            }
            if (sortConfig.key === 'fournisseur_nom') {
                return sortConfig.direction === 'asc'
                    ? a.fournisseur_nom?.localeCompare(b.fournisseur_nom)
                    : b.fournisseur_nom?.localeCompare(a.fournisseur_nom);
            }
            return 0;
        });

    const moyenneGenerale = evaluations.length > 0 
        ? (evaluations.reduce((acc, e) => acc + (e.note || 0), 0) / evaluations.length).toFixed(1)
        : 0;

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des évaluations...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchEvaluations}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-star-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Évaluation des fournisseurs</h4>
                                    <p className="text-muted small mb-0">
                                        {evaluations.length} évaluation(s) • Note moyenne: {moyenneGenerale}/5
                                    </p>
                                </div>
                            </div>
                        </div>
                        {/* <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/achats/fournisseurs')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div> */}
                    </div>

                    {/* KPI Cards modernes */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm={6} xl={4}>
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon">
                                        <i className="ri-star-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Note moyenne</span>
                                        <h3 className="kpi-value">{stats.note_moyenne ? parseFloat(stats.note_moyenne).toFixed(1) : 'N/A'}</h3>
                                        <span className="kpi-unit">/5 satisfaction</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={4}>
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon">
                                        <i className="ri-checkbox-circle-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Fournisseurs évalués</span>
                                        <h3 className="kpi-value">{new Set(evaluations.map(e => e.id_fournisseur)).size}</h3>
                                        <span className="kpi-unit">sur {stats.total_fournisseurs || 0}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={4}>
                                <div className="kpi-card kpi-info">
                                    <div className="kpi-icon">
                                        <i className="ri-calendar-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Dernière évaluation</span>
                                        <h3 className="kpi-value" style={{ fontSize: '1.1rem' }}>
                                            {evaluations[0] ? formatDate(evaluations[0].date_evaluation) : '-'}
                                        </h3>
                                        <span className="kpi-unit">la plus récente</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Filtre recherche */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={8}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par fournisseur, code, commentaire..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={4} className="d-flex justify-content-end align-items-center">
                                    <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                        <i className="ri-stack-line me-1"></i>
                                        {filteredEvaluations.length} évaluation(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau des évaluations */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Fournisseur</th>
                                            <SortableHeader field="date_evaluation">Date</SortableHeader>
                                            <SortableHeader field="note">Note</SortableHeader>
                                            <th>Délais</th>
                                            <th>Qualité</th>
                                            <th>Prix</th>
                                            <th>Commentaire</th>
                                            <th className="pe-4">Évaluateur</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEvaluations.length > 0 ? (
                                            filteredEvaluations.map((e) => {
                                                const noteBadge = getNoteBadge(e.note);
                                                return (
                                                    <tr key={e.id_evaluation} className="evaluation-row">
                                                        <td className="ps-4">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="avatar-icon">
                                                                    <i className="ri-truck-line"></i>
                                                                </div>
                                                                <div>
                                                                    <Link to={`/achats/fournisseurs/${e.id_fournisseur}`} className="table-link">
                                                                        {e.fournisseur_nom}
                                                                    </Link>
                                                                    <div><small className="text-muted">{e.code_fournisseur}</small></div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-1">
                                                                <i className="ri-calendar-line text-muted"></i>
                                                                <span>{formatDate(e.date_evaluation)}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="note-value">{e.note}/5</div>
                                                                <div className="stars-container">
                                                                    {getNoteStars(e.note)}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip>Délais: {e.critere_delai}/5</Tooltip>}
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <i className={getCriterionIcon(e.critere_delai)} style={{ color: getCriterionColor(e.critere_delai) }}></i>
                                                                    <div className="progress-bar-custom" style={{ width: '60px' }}>
                                                                        <div className="progress-fill" style={{ width: `${e.critere_delai * 20}%`, background: getCriterionColor(e.critere_delai) }}></div>
                                                                    </div>
                                                                </div>
                                                            </OverlayTrigger>
                                                       </td>
                                                        <td>
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip>Qualité: {e.critere_qualite}/5</Tooltip>}
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <i className={getCriterionIcon(e.critere_qualite)} style={{ color: getCriterionColor(e.critere_qualite) }}></i>
                                                                    <div className="progress-bar-custom" style={{ width: '60px' }}>
                                                                        <div className="progress-fill" style={{ width: `${e.critere_qualite * 20}%`, background: getCriterionColor(e.critere_qualite) }}></div>
                                                                    </div>
                                                                </div>
                                                            </OverlayTrigger>
                                                       </td>
                                                        <td>
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip>Prix: {e.critere_prix}/5</Tooltip>}
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <i className={getCriterionIcon(e.critere_prix)} style={{ color: getCriterionColor(e.critere_prix) }}></i>
                                                                    <div className="progress-bar-custom" style={{ width: '60px' }}>
                                                                        <div className="progress-fill" style={{ width: `${e.critere_prix * 20}%`, background: getCriterionColor(e.critere_prix) }}></div>
                                                                    </div>
                                                                </div>
                                                            </OverlayTrigger>
                                                        </td>
                                                        <td>
                                                            {e.commentaire ? (
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={<Tooltip>{e.commentaire}</Tooltip>}
                                                                >
                                                                    <span className="comment-preview">
                                                                        <i className="ri-chat-1-line me-1 text-muted"></i>
                                                                        {e.commentaire.length > 30 ? e.commentaire.substring(0, 30) + '...' : e.commentaire}
                                                                    </span>
                                                                </OverlayTrigger>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td className="pe-4">
                                                            <div className="d-flex align-items-center gap-1">
                                                                <i className="ri-user-line text-muted"></i>
                                                                <span className="small">{e.prenom_utilisateur} {e.nom_utilisateur}</span>
                                                            </div>
                                                       </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-star-line empty-icon text-warning" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucune évaluation trouvée</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Commencez par évaluer vos fournisseurs"}
                                                        </p>
                                                        {filterText ? (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                                className="rounded-pill"
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="primary"
                                                                onClick={() => navigate('/achats/fournisseurs')}
                                                                className="rounded-pill"
                                                                style={{ background: '#21ACCD', border: 'none' }}
                                                            >
                                                                <i className="ri-arrow-left-line me-2"></i>Voir les fournisseurs
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-info { border-left-color: #0ea5e9; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Avatar Icon */
                .avatar-icon {
                    width: 36px;
                    height: 36px;
                    background: #f1f5f9;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #21ACCD;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Note styles */
                .note-value {
                    font-weight: 700;
                    font-size: 0.9rem;
                    min-width: 40px;
                }
                
                .stars-container {
                    display: inline-flex;
                }
                
                /* Custom Progress Bar */
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                /* Comment Preview */
                .comment-preview {
                    cursor: pointer;
                    display: inline-block;
                    max-width: 150px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    color: #475569;
                }
                
                .comment-preview:hover {
                    color: #21ACCD;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .evaluation-row {
                    transition: all 0.2s ease;
                }
                
                .evaluation-row:hover {
                    background: #f8fafc;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}